import React, { useState, useMemo, useEffect } from 'react';

import PropTypes from 'prop-types';

import { AccordionItem } from '@adsk/alloy-react-accordion';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';
import SelectBox, { VirtualizedSelectBox } from '@adsk/alloy-select-box';

const getOptions = (groupedOptions) => {
  return groupedOptions.reduce((pre, { key, label, options }) => {
    if (options.length > 0) pre.push({ label, isGroupHeader: true, key });
    pre.push(
      ...options.map((item) => ({ ...item, isGroupHeader: false, key })),
    );

    return pre;
  }, []);
};

const filterOptions = (options, inputValue, selectProps, groupedOptions) => {
  const search = inputValue.trim().toLowerCase();
  if (!search) return options;

  const { labelKey, labelGetter, subtextKey, subtextGetter } = selectProps;

  const showHeader = groupedOptions.reduce((pre, { key, options }) => {
    pre[key] =
      SelectBox.defaultFilterOptions(options, search, selectProps)?.length > 0;

    return pre;
  }, {});

  return options.reduceRight((total, option) => {
    const { isGroupHeader, key } = option;

    if (isGroupHeader) return showHeader[key] ? [option, ...total] : total;

    const label = labelGetter ? labelGetter(option) : option[labelKey];
    const subtext = subtextGetter ? subtextGetter(option) : option[subtextKey];

    if (
      (label && label.toLowerCase().indexOf(search) >= 0) ||
      (subtext && subtext.toLowerCase().indexOf(search) >= 0)
    ) {
      return [option, ...total];
    }

    return total;
  }, []);
};

const AccordionSelectBox = ({ groupedOptions, onChange, ...rest }) => {
  const initialOptions = useMemo(
    () => getOptions(groupedOptions || []),
    [groupedOptions],
  );
  const initialExpandedKey = useMemo(
    () => (groupedOptions || []).map(({ key }) => key)[0],
    [groupedOptions],
  );

  const [expandedKey, setExpandedKey] = useState(initialExpandedKey);

  const options = useMemo(
    () =>
      initialOptions.filter(
        ({ isGroupHeader, key }) => isGroupHeader || key === expandedKey,
      ),
    [initialOptions, expandedKey],
  );

  useEffect(() => {
    setExpandedKey(initialExpandedKey);
  }, [initialExpandedKey]);

  const renderItem = (args) => {
    const { item, index } = args;

    if (item.isGroupHeader) {
      return (
        <AccordionItem
          key={index}
          style={{
            button: {
              '> svg': {
                flex: 'none',
              },
              '> div:first-of-type': {
                maxWidth: 'calc(100% - 36px)',
              },
            },
          }}
          title={<OverflowTooltip>{item.label}</OverflowTooltip>}
          expanded={item.key === expandedKey}
          onExpand={() =>
            setExpandedKey((expandedKey) =>
              expandedKey && expandedKey === item.key ? null : item.key,
            )
          }
        />
      );
    }

    return SelectBox.defaultItemRenderer(args);
  };

  const handleChange = (value) =>
    onChange(
      Array.isArray(value)
        ? value.filter(({ isGroupHeader }) => !isGroupHeader)
        : value,
    );

  return (
    <VirtualizedSelectBox
      options={options}
      onChange={handleChange}
      itemRenderer={renderItem}
      filterOptions={(options, search, selectProps) =>
        filterOptions(options, search, selectProps, groupedOptions)
      }
      {...rest}
    />
  );
};

AccordionSelectBox.propTypes = {
  /**
   * grouped options
   */
  groupedOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      options: PropTypes.array,
    }),
  ),
  /**
   * callback when selected value changed
   */
  onChange: PropTypes.func,
};

export default AccordionSelectBox;
